// pages/AttemptPlayer/components/widgets/FeedbackModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaVolumeUp } from "react-icons/fa";
import type {
  Response,
  ResponseSelect,
  ResponseNumeric,
  ResponseText,
  ResponseVoice,
  ResponseCanvas,
  Slide,
} from "../../../ManageQuestions/types";
import "../widgets/FeedbackModal.scss";

/* ---------- small utils ---------- */
const norm = (s: string) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^0-9a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const speak = (t: string) => {
  try {
    const u = new SpeechSynthesisUtterance(t);
    // Force English (pilot voice) — without this the browser uses its default
    // voice, which speaks Dutch on nl-locale systems.
    u.lang = "en-US";
    const voices = window.speechSynthesis.getVoices() || [];
    const en = voices.filter((v) => v.lang?.toLowerCase().startsWith("en"));
    const male = en.find((v) =>
      /david|mark|guy|alex|daniel|fred|george|james|male/i.test(v.name),
    );
    const pick = male || en[0];
    if (pick) u.voice = pick;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  } catch { }
};

type Props = {
  show: boolean;
  slide: Slide;
  response: Response;
  userAnswer: any | undefined;
  onClose: () => void;

  // optional hooks you already pass from player
  onMarkCorrect?: () => void;
  onApplyVoiceCorrection?: (payload: { transcript: string }) => void;
  onHearExpected?: () => void;
  onHearUser?: () => void;
};

/* ===================== VOICE SECTION (server-driven) ===================== */
type VoiceProps = Required<Pick<Props, "show" | "slide" | "userAnswer" | "onClose">> & {
  response: ResponseVoice;
  onMarkCorrect?: Props["onMarkCorrect"];
  onApplyVoiceCorrection?: Props["onApplyVoiceCorrection"];
  onHearExpected?: Props["onHearExpected"];
  onHearUser?: Props["onHearUser"];
};

const VoiceSection: React.FC<VoiceProps> = ({
  show,
  slide,
  response,
  userAnswer,
  onClose,
  onMarkCorrect,
  onApplyVoiceCorrection,
  onHearExpected,
  onHearUser,
}) => {
  const ua = userAnswer || {};
  const ur = ua.user_response || {};
  const serverEval: any =
    ur.server_eval || ua.auto_grade?.server_eval || ua.auto_grade || null;

  const serverText: string = ur.server_text || ur.transcript || "";
  const audioUrl: string | undefined = ur.url;

  const ek = (response?.evaluation?.expected_keywords || []) as Array<{ any_of?: string[] }>;
  const labels = (response?.evaluation as any)?.prompt_list as string[] | undefined;

  type Group = { label: string; tokens: string[] };
  const groups: Group[] = (ek.length ? ek : [{ any_of: [""] }]).map((g, i) => {
    const phrase = (g.any_of?.[0] || "").trim();
    const tokens = phrase ? phrase.split(/\s+/).filter(Boolean) : [];
    const label =
      (labels && labels[i]) ||
      (response as any).feedback_sections?.[i] ||
      `Part ${i + 1}`;
    return { label, tokens };
  });

  const allExpectedTokens = groups.flatMap((g) => g.tokens);
  const expectedSentence = groups.map((g) => g.tokens.join(" ")).join(". ").trim();

  const best: Array<{ keyword: string; flag?: string }> = Array.isArray(serverEval?.best_result)
    ? serverEval.best_result
    : [];

  const correctSet = React.useMemo(() => {
    const s = new Set<string>();
    if (best.length) {
      best
        .filter((x) => String(x.flag || "").toUpperCase() === "CORRECT")
        .forEach((x) => s.add(norm(x.keyword)));
    } else {
      const inUser = new Set((serverText || "").split(/\s+/).map(norm).filter(Boolean));
      allExpectedTokens.forEach((t) => {
        if (inUser.has(norm(t))) s.add(norm(t));
      });
    }
    return s;
  }, [best, serverText, allExpectedTokens]);

  // When nothing is missing the whole answer matched — colour every token green
  // even if the authored wording differs from the spoken digits (e.g. "thousand"
  // vs "zero zero zero"), so the score (100%) and the colours stay consistent.
  const allCorrect =
    best.length > 0 &&
    (serverEval?.missed_keyword_count ?? 0) === 0 &&
    best.every((x) => String(x.flag || "").toUpperCase() === "CORRECT");

  type WordState = "ok" | "bad" | "edit";

  const initialStates = React.useMemo<WordState[]>(() => {
    if (!allExpectedTokens.length) return [];
    return allExpectedTokens.map((t) => ((allCorrect || correctSet.has(norm(t))) ? "ok" : "bad"));
  }, [allExpectedTokens, correctSet]);

  // NEW: corrected token text buffer; starts as expected tokens
  const [corrected, setCorrected] = useState<string[]>(allExpectedTokens);

  const [perWordState, setPerWordState] = useState<WordState[]>(initialStates);
  const [selIdx, setSelIdx] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>("");

  // --- build a stable signature of the data we want to reset on ---
  const resetSig = React.useMemo(() => {
    const bestSig = Array.isArray(best)
      ? best.map(b => `${b.keyword}:${String(b.flag || "")}`).join("|")
      : "";
    const tokensSig = allExpectedTokens.join(" ");
    return JSON.stringify({
      show,
      serverText,   // transcript we got
      bestSig,      // which words the server marked
      tokensSig,    // expected tokens (flat)
    });
  }, [show, serverText, best, allExpectedTokens]);

  // --- only reset when signature actually changes (prevents loops) ---
  const lastResetSigRef = React.useRef<string>("");
  useEffect(() => {
    if (!show) return;                     // don’t do anything while hidden
    if (lastResetSigRef.current === resetSig) return;

    // compute fresh initial state exactly once per new signature
    const freshStates: WordState[] = allExpectedTokens.map(t =>
      (allCorrect || correctSet.has(norm(t))) ? "ok" : "bad"
    );

    setPerWordState(freshStates);
    setCorrected(allExpectedTokens);
    setSelIdx(null);
    setEditValue("");

    lastResetSigRef.current = resetSig;
    // no other deps; reset is guarded by the signature itself
  }, [resetSig, show]);

  // reset on open/data change
  // useEffect(() => {
  //   if (!show) return;
  //   setPerWordState(initialStates);
  //   setCorrected(allExpectedTokens);
  //   setSelIdx(null);
  //   setEditValue("");
  // }, [show, initialStates, allExpectedTokens]);


  // keep manual correction indices
  useEffect(() => {
    try {
      const list = perWordState.map((s, i) => (s === "edit" ? i : -1)).filter((i) => i >= 0);
      if (userAnswer) {
        userAnswer.user_response = userAnswer.user_response || {};
        userAnswer.user_response.manual_corrections = list;
      }
    } catch { }
  }, [perWordState, userAnswer]);

  const scorePct = Number.isFinite(serverEval?.score)
    ? Math.round(serverEval.score)
    : Math.round(
      ((perWordState.filter((s) => s === "ok" || s === "edit").length || 0) * 100) /
      Math.max(1, perWordState.length)
    );

  const yourTokens = React.useMemo(() => (serverText || "").split(/\s+/).filter(Boolean), [serverText]);

  const hearUser = () => {
    if (onHearUser) return onHearUser();
    if (!audioUrl) return;
    try { new Audio(audioUrl).play().catch(() => { }); } catch { }
  };
  const hearExpected = () => {
    if (onHearExpected) return onHearExpected();
    if (expectedSentence) speak(expectedSentence);
  };
  const hearCorrected = () => {
    const sentence = (() => {
      const out: string[] = [];
      let offset = 0;
      groups.forEach((g, i) => {
        const part = corrected.slice(offset, offset + g.tokens.length).join(" ").trim();
        if (part) out.push(part);
        offset += g.tokens.length;
      });
      return out.join(". ");
    })();
    if (sentence) speak(sentence);
  };

  const title =
    (response as any).feedback_heading ||
    (slide as any)?.meta?.feedback_heading ||
    "Keyword Feedback";

  const groupOffsets = React.useMemo(() => {
    const arr: number[] = [];
    let acc = 0;
    groups.forEach((g) => { arr.push(acc); acc += g.tokens.length; });
    return arr;
  }, [groups]);

  const tokState = (gIdx: number, tIdx: number): WordState =>
    perWordState[groupOffsets[gIdx] + tIdx];

  const onTokenClick = (gIdx: number, tIdx: number) => {
    const flat = groupOffsets[gIdx] + tIdx;
    setSelIdx(flat);
    setEditValue(corrected[flat] || groups[gIdx].tokens[tIdx] || "");
  };

  const applyTokenEdit = () => {
    if (selIdx == null) return;
    const value = (editValue || "").trim();
    if (!value) return;
    setCorrected((prev) => {
      const next = [...prev];
      next[selIdx] = value;
      return next;
    });
    setPerWordState((prev) => {
      const next = [...prev];
      if (next[selIdx] === "bad") next[selIdx] = "edit"; // move to corrected
      return next;
    });
  };

  const resetTokenEdit = () => {
    if (selIdx == null) return;
    setCorrected((prev) => {
      const next = [...prev];
      next[selIdx] = allExpectedTokens[selIdx];
      return next;
    });
    setPerWordState((prev) => {
      const next = [...prev];
      if (!correctSet.has(norm(allExpectedTokens[selIdx]))) next[selIdx] = "bad";
      else next[selIdx] = "ok";
      return next;
    });
    setEditValue(allExpectedTokens[selIdx] || "");
  };

  // Final corrected transcript (grouped into sentences)
  const correctedTranscript = React.useMemo(() => {
    const out: string[] = [];
    let offset = 0;
    groups.forEach((g) => {
      const seg = corrected.slice(offset, offset + g.tokens.length).join(" ").trim();
      if (seg) out.push(seg);
      offset += g.tokens.length;
    });
    return out.join(". ");
  }, [groups, corrected]);

  const titleBarRight = (
    <div className="d-flex align-items-center gap-2">
    </div>
  );

  return (
    <Modal show={show} onHide={onClose} centered size="lg" className="tap-feedback-modal tap-feedback--white">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
        <div className="ms-auto">{titleBarRight}</div>
      </Modal.Header>

      <Modal.Body>
        <div className="fb-expected-line--bar">
          <span className="legend dot ok">CORRECT</span>
          <span className="legend dot edit">CORRECTED</span>
          <span className="legend dot bad">INCORRECT/MISSING</span>
        </div>

        <div className="fb-groups">
          {groups.map((g, gi) => (
            <div className="fb-group" key={`g-${gi}`}>
              <div className="fb-group-label">{g.label}</div>
              <div className="fb-group-line">
                {g.tokens.map((tok, ti) => {
                  const flat = groupOffsets[gi] + ti;
                  const st = tokState(gi, ti);
                  const cls = st === "ok" ? "ok" : st === "edit" ? "edit" : "bad";
                  return (
                    <button
                      key={`${tok}-${ti}`}
                      className={`fb-token-inline ${cls}`}
                      onClick={() => onTokenClick(gi, ti)}
                      title={
                        st === "bad"
                          ? "Incorrect/Missing — click to edit & correct"
                          : st === "ok"
                            ? "Matched"
                            : "Corrected"
                      }
                    >
                      {corrected[flat] || tok}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="fb-score-row">
          <div className="fb-score">Score: {scorePct}%</div>
        </div>

        <div className="fb-bigcard">
          {selIdx == null ? (
            <div className="bigword"><span className="placeholder">Tap a word for details…</span></div>
          ) : (
            <>
              <div
                className={
                  "bigword " +
                  (perWordState[selIdx] === "bad"
                    ? "is-missing"
                    : perWordState[selIdx] === "edit"
                      ? "is-corrected"
                      : "is-correct")
                }
              >
                {corrected[selIdx]}
              </div>

              {/* inline editor for corrected word */}
              <div className="fb-inline-editor mt-2">
                {/* <input
                  className="fb-edit-input"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="Type corrected word…"
                  onKeyDown={(e) => { if (e.key === "Enter") applyTokenEdit(); }}
                /> */}
                <div className="d-flex gap-2 mt-2">
                  <Button size="sm" variant="primary" onClick={applyTokenEdit}>
                    Save Correction
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => speak(corrected[selIdx])}>
                    <FaVolumeUp className="me-1" /> Hear Word
                  </Button>
                  <Button size="sm" variant="outline-dark" onClick={resetTokenEdit}>
                    Reset
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="fb-expected-title" style={{ marginTop: 4, fontWeight: 800 }}>
          Your Script
        </div>
        <div className="your-script-line">
          {yourTokens.length ? (
            yourTokens.map((t, i) => (
              <span key={`${t}-${i}`} className={`ys-token ${(allCorrect || correctSet.has(norm(t))) ? "ok" : "bad"}`}>
                {t}
              </span>
            ))
          ) : (
            <span className="ys-token bad">—</span>
          )}
        </div>

        <div className="fb-practice-card">
          <div className="title">{expectedSentence || "Practice"}</div>
          <div className="links">
            <button className="linkish" onClick={hearExpected}>
              <FaVolumeUp className="me-1" /> Hear Expected Response
            </button>
            {/* <button className="linkish" onClick={hearCorrected} disabled={!correctedTranscript}>
              <FaVolumeUp className="me-1" /> Hear Corrected Response
            </button> */}
            <button className="linkish" onClick={hearUser} disabled={!audioUrl}>
              <FaVolumeUp className="me-1" /> Hear Your Response
            </button>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <div className="text-muted small">
          {/* {onApplyVoiceCorrection
            ? "Apply will save your corrected transcript."
            : ""} */}
        </div>
        <div className="d-flex gap-2">
          {/* {onApplyVoiceCorrection && (
            <Button
              variant="primary"
              onClick={() => onApplyVoiceCorrection?.({ transcript: correctedTranscript })}
              disabled={!correctedTranscript}
              title="Save corrected transcript to the attempt"
            >
              Apply Corrections
            </Button>
          )} */}
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};


/* ===================== NON-VOICE SECTION (unchanged logic) ===================== */
const NonVoiceSection: React.FC<
  Required<Pick<Props, "show" | "slide" | "response" | "userAnswer" | "onClose">>
> = ({ show, slide, response, userAnswer, onClose }) => {
  const title =
    (response as any).feedback_heading ||
    (slide as any)?.meta?.feedback_heading ||
    slide.title ||
    "Feedback";

  const labels = {
    expected: (response as any).feedback_labels?.expected_label || "Expected",
    yours: (response as any).feedback_labels?.your_label || "Your answer",
  };

  const rows: Array<{ k: string; v: React.ReactNode; intent?: "ok" | "bad" | "" }> = [];

  if (response.type === "select") {
    const rr = response as ResponseSelect;
    const correct = (rr.options || []).filter((o: any) => o.is_correct).map((o: any) => o.label || o.id);
    const chosenIds = (userAnswer?.user_response?.selected || []) as string[];
    const chosen = (rr.options || []).filter((o: any) => chosenIds.includes(o.id)).map((o: any) => o.label || o.id);
    const ok = correct.length && correct.every((lbl: string) => chosen.includes(lbl)) && chosen.length === correct.length;
    rows.push({ k: labels.expected, v: correct.join(", ") || "—" });
    rows.push({ k: labels.yours, v: chosen.join(", ") || "—", intent: ok ? "ok" : "bad" });
  }

  if (response.type === "numeric") {
    const rr = response as ResponseNumeric;
    const rng = rr.evaluation?.range;
    const exp =
      rng?.target != null && rng?.tolerance != null
        ? `Target ${rng.target} ± ${rng.tolerance}`
        : `Range ${rng?.min ?? "-∞"} – ${rng?.max ?? "+∞"}`;
    rows.push({ k: labels.expected, v: exp });
    rows.push({ k: labels.yours, v: String(userAnswer?.user_response?.value ?? "—") });
  }

  if (response.type === "text") {
    const rr = response as ResponseText;
    const exp = rr.evaluation?.text || (rr.evaluation?.patterns || []).join(" | ") || "—";
    const yours = userAnswer?.user_response?.text || "—";
    rows.push({ k: labels.expected, v: exp });
    rows.push({ k: labels.yours, v: yours, intent: norm(yours) === norm(exp) ? "ok" : "bad" });
  }

  if (response.type === "canvas_action") {
    const rr = response as ResponseCanvas;
    const ids = rr.evaluation?.correct_entity_ids || [];
    const sel = (userAnswer?.user_response?.selected || []) as string[];
    const ok = ids.length && ids.every((id: string) => sel.includes(id)) && sel.length === ids.length;
    rows.push({ k: labels.expected, v: ids.join(", ") || "—" });
    rows.push({ k: labels.yours, v: sel.join(", ") || "—", intent: ok ? "ok" : "bad" });
  }

  return (
    <Modal show={show} onHide={onClose} centered size="lg" className="tap-feedback-modal tap-feedback--white">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="fb-rows">
          {rows.map((r, i) => (
            <div className="fb-row" key={i}>
              <div className="fb-row-label">{r.k}</div>
              <div className={`fb-row-value ${r.intent || ""}`}>{r.v}</div>
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/* ===================== PARENT (no conditional hooks) ===================== */
const FeedbackModal: React.FC<Props> = (props) => {
  if (props.response.type === "voice") {
    return (
      <VoiceSection
        show={props.show}
        slide={props.slide}
        response={props.response as ResponseVoice}
        userAnswer={props.userAnswer}
        onClose={props.onClose}
        onApplyVoiceCorrection={props.onApplyVoiceCorrection}
        onMarkCorrect={props.onMarkCorrect}
        onHearExpected={props.onHearExpected}
        onHearUser={props.onHearUser}
      />
    );
  }
  return (
    <NonVoiceSection
      show={props.show}
      slide={props.slide}
      response={props.response}
      userAnswer={props.userAnswer}
      onClose={props.onClose}
    />
  );
};

export default FeedbackModal;
